import { useCallback, useEffect, useState } from "react";
import { Loader2, Sun, Sunset } from "lucide-react";
import { useLang } from "@/i18n";
import { http, errText, SIGN_KEYS, karanaName, tithiName } from "@/lib/api";
import { Field, PlaceInput } from "@/components/tools/BirthFields";
import { FormError, PageShell } from "@/components/site/PageShell";

const GHAZIABAD = { name: "Ghāziābād", country: "India", lat: 28.66535, lon: 77.43915, tz: "Asia/Kolkata" };
const GOOD = new Set(["amrit", "shubh", "labh", "char"]);
const todayStr = () => new Date().toLocaleDateString("en-CA");

const Chog = ({ t, title, rows, testid }) => (
  <div className="card p-6" data-testid={testid}>
    <h3 className="display text-2xl mb-4">{title}</h3>
    <ul className="divide-y divide-[color:var(--line)]">
      {rows.map((r) => (
        <li key={r.start} className="flex justify-between py-2.5 text-sm">
          <span className={GOOD.has(r.name) ? "text-emerald-700 font-semibold" : "text-[color:var(--muted)]"}>
            {t.names.chog[r.name]} <span className="text-xs font-normal">· {GOOD.has(r.name) ? t.panchang.good : t.panchang.bad}</span>
          </span>
          <span>{r.start} – {r.end}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Facts = ({ t, d }) => {
  const rows = [
    [t.panchang.vara, t.names.weekdays[d.weekday]],
    [t.panchang.tithi, `${tithiName(t, d.tithi.index)} · ${t.panchang.paksha[d.tithi.paksha]}`],
    [t.panchang.nakshatra, `${t.names.nakshatras[d.nakshatra.index]} (${d.nakshatra.pada})`],
    [t.panchang.yoga, t.names.yogas[d.yoga]],
    [t.panchang.karana, karanaName(t, d.karana)],
    [t.panchang.moonSign, t.rashifal.signs[SIGN_KEYS[d.moon_sign]]],
    [t.panchang.rahu, `${d.rahu_kaal.start} – ${d.rahu_kaal.end}`],
    [t.panchang.abhijit, `${d.abhijit.start} – ${d.abhijit.end}`],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line)] border border-[color:var(--line)] rounded-3xl overflow-hidden" data-testid="panchang-facts">
      {rows.map(([k, v]) => <div key={k} className="bg-white p-5"><p className="lbl">{k}</p><p className="display text-xl">{v}</p></div>)}
    </div>
  );
};

export default function PanchangPage() {
  const { t } = useLang();
  const [date, setDate] = useState(todayStr());
  const [place, setPlace] = useState(GHAZIABAD);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const errMsg = t.common.error;
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await http.get("/panchang", { params: { date, lat: place.lat, lon: place.lon, tz: place.tz } });
      setState({ loading: false, error: "", data: r.data });
    } catch (err) {
      setState({ loading: false, error: errText(err, errMsg), data: null });
    }
  }, [date, place, errMsg]);
  useEffect(() => { load(); }, [load]);
  const d = state.data;
  return (
    <PageShell eyebrow={t.panchang.eyebrow} title={t.panchang.title} desc={t.panchang.desc} testid="panchang">
      <div className="card p-6 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <Field label={t.panchang.date}><input type="date" data-testid="panchang-date" className="field" value={date} onChange={(e) => setDate(e.target.value || todayStr())} /></Field>
        <Field label={t.panchang.place}><PlaceInput testid="panchang-place" value={place} onChange={(p) => p && setPlace(p)} placeholder={t.common.placeHint} /></Field>
      </div>
      <div className="mt-10">
        <FormError msg={state.error} />
        {state.loading && !d && <Loader2 className="w-6 h-6 animate-spin text-[color:var(--saffron)]" />}
        {d && (
          <div className={`space-y-8 transition-opacity ${state.loading ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap gap-3">
              <span className="chip" data-testid="panchang-sunrise"><Sun className="w-4 h-4 mr-2 text-[color:var(--saffron)]" />{t.panchang.sunrise}: <b className="ml-1">{d.sunrise}</b></span>
              <span className="chip" data-testid="panchang-sunset"><Sunset className="w-4 h-4 mr-2 text-[color:var(--saffron)]" />{t.panchang.sunset}: <b className="ml-1">{d.sunset}</b></span>
            </div>
            <Facts t={t} d={d} />
            <div className="grid lg:grid-cols-2 gap-8">
              <Chog t={t} title={t.panchang.chogDay} rows={d.choghadiya_day} testid="chog-day" />
              <Chog t={t} title={t.panchang.chogNight} rows={d.choghadiya_night} testid="chog-night" />
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
